import { useCallback } from 'react';
import type { FormEvent } from 'react';
import {
  Banner,
  Box,
  Button,
  Flex,
  Form,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Spinner,
  Text,
  TextField,
  useModal,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { navigateExternal } from '@/utils/navigate';
import { useReauthFlow, type ReauthFlow, type ReauthUser } from '../use-reauth-flow';
import { SsoSignInButton } from './sso-sign-in-button';

const RECOVERY_CODE_DOCS_URL = 'https://docs.customer.io/two-factor-auth/#i-lost-my-deviceim-locked-out-what-do-i-do';

/**
 * Data passed to the managed reauth modal at `open()` time. `useAuth()` now
 * resolves inside managed modals (it reads the global account/user stores), but
 * this flow deliberately threads the specific user + environment being
 * re-authenticated: it runs on a session rejection, from the layout's retained
 * last-good resolution captured at `open()` time, rather than whatever the auth
 * stores hold when the modal renders.
 */
export type ReauthModalData = {
  user: ReauthUser;
  /**
   * The last-good environment id, for scoping the `re_authenticated` analytics
   * event, captured at `open()` time. `null` when unknown (the event is then
   * skipped).
   */
  environmentId: string | null;
};

type StepProps = {
  flow: ReauthFlow;
  email: string;
};

type StepFormProps = {
  flow: ReauthFlow;
  children: React.ReactNode;
};

function StepForm({ flow, children }: StepFormProps) {
  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      void flow.submit?.();
    },
    [flow],
  );

  return (
    <Form onSubmit={handleSubmit} autoLoading={false}>
      {children}
    </Form>
  );
}

function LoadingStep() {
  return (
    <Flex justifyContent="center">
      <Spinner m="200" />
    </Flex>
  );
}

function PasswordStep({ flow, email }: StepProps) {
  return (
    <StepForm flow={flow}>
      <Paragraph>
        To continue as <Text text="product-p-active">{email}</Text>, re-enter your password.
      </Paragraph>
      <Box mt="150" pb="300">
        <TextField
          type="password"
          label="Password"
          autoFocus
          value={flow.password}
          onInput={(event) => {
            flow.setPassword(
              // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
              (event.target as HTMLInputElement).value,
            );
          }}
          error={flow.error}
          data-testid="reauth-password-input"
        />
      </Box>
    </StepForm>
  );
}

function TwoFactorStep({ flow }: StepProps) {
  return (
    <StepForm flow={flow}>
      <Paragraph>Enter your two factor authentication code.</Paragraph>
      <TextField
        label="Authentication code"
        autoFocus
        my="150"
        value={flow.twoFactorCode}
        onInput={(event) => {
          flow.setTwoFactorCode(
            // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
            (event.target as HTMLInputElement).value,
          );
        }}
        error={flow.error}
        data-testid="reauth-2fa-input"
      />
      <Paragraph variant="secondary">
        If you don't have access to your authenticator app, you can enter a{' '}
        <Link href={RECOVERY_CODE_DOCS_URL} isExternal>
          one-time recovery code
        </Link>
        .
      </Paragraph>
    </StepForm>
  );
}

function SsoStep({ flow, email }: StepProps) {
  return (
    <>
      <Paragraph data-testid="reauth-sso-prompt">
        To continue as <Text text="product-p-active">{email}</Text>, sign in.
      </Paragraph>
      {flow.error != null ? (
        <Banner variant="critical" mt="150">
          {flow.error}
        </Banner>
      ) : null}
    </>
  );
}

function EmailLinkSentStep({ email }: StepProps) {
  return (
    <>
      <Heading level="3" mb="100">
        Check your email
      </Heading>
      <Paragraph data-testid="reauth-email-link-sent">
        We sent an email to <Text text="product-p-active">{email}</Text>. Select the link to finish signing in.
      </Paragraph>
    </>
  );
}

const STEP_BODY: Partial<Record<ReauthFlow['step'], (props: StepProps) => React.ReactNode>> = {
  loading: LoadingStep,
  password: PasswordStep,
  sso: SsoStep,
  two_factor_code: TwoFactorStep,
  email_link_sent: EmailLinkSentStep,
};

type ReauthFooterProps = {
  flow: ReauthFlow;
  onLogout: () => void;
};

function ReauthFooter({ flow, onLogout }: ReauthFooterProps) {
  if (flow.step === 'loading') {
    return null;
  }

  // Ember styles Log out per content component, not per step: subtle in the SSO
  // variant (both the sign-in and 2FA sub-steps), the Button default (secondary)
  // in the password variant. Keying on `isSsoFlow`, not `step`, keeps the 2FA
  // sub-step's button matching the variant it was reached through.
  const logout = (
    <Button isCritical={true} variant={flow.isSsoFlow ? 'tertiary' : 'secondary'} onClick={onLogout}>
      Log out
    </Button>
  );

  // No confirm action on the email-link step — the user acts from their inbox.
  if (flow.step === 'email_link_sent') {
    return <ModalFooter>{logout}</ModalFooter>;
  }

  if (flow.step === 'sso') {
    return (
      <ModalFooter alignItems="center">
        {logout}
        <SsoSignInButton
          ssoConfiguration={flow.ssoConfiguration}
          isGoogle={flow.isGoogleReauth}
          isLoading={flow.isSubmitting}
          onClick={() => void flow.submit?.()}
        />
      </ModalFooter>
    );
  }

  const submitDisabled = flow.step === 'password' ? isEmptyString(flow.password) : isEmptyString(flow.twoFactorCode);
  return (
    <ModalFooter>
      {logout}
      <Button
        variant="primary"
        isDisabled={submitDisabled}
        isLoading={flow.isSubmitting}
        onClick={() => void flow.submit?.()}
        data-testid="reauth-submit"
      >
        Log in
      </Button>
    </ModalFooter>
  );
}

/**
 * In-place re-authentication for an expired session (FPS-2996 follow-up): the
 * app stays mounted behind a non-dismissable modal, the user re-enters
 * credentials (password or SSO, with the 2FA and email-link sub-steps), and
 * work in progress survives.
 *
 * A managed modal (opened via the modal-manager). The authenticated layout
 * opens it on a session rejection and closes it on recovery (`useReauthModal` +
 * `closeAllOfType`); a fresh instance mounts per open, so the flow state is
 * never stale. See `useReauthFlow` for the recovery semantics.
 */
export function ReauthModal({ data, modalState }: PlumaManagedModalProps<ReauthModalData>) {
  const { user, environmentId } = data;
  const flow = useReauthFlow(user, environmentId);
  const StepBody = STEP_BODY[flow.step];

  const logout = useCallback(() => {
    navigateExternal('/logout');
  }, []);

  return (
    <Modal state={modalState} isDismissable={false} shouldScrollInViewport={false} title="Your session has expired">
      <ModalBody>{StepBody != null ? <StepBody flow={flow} email={user.email} /> : null}</ModalBody>
      <ReauthFooter flow={flow} onLogout={logout} />
    </Modal>
  );
}

/** Bound modal-manager handle for the reauth modal (`open`/`closeAllOfType`). */
export function useReauthModal() {
  return useModal(ReauthModal);
}
